import Link from 'next/link';
import styled from 'styled-components';

import { buildPTProductTypeLink } from 'productTemplate/utils/buildPTProductTypeLink';
import { findParentProductCategory } from 'productTemplate/utils/findParentProductCategory';
import { formatStringLowercaseWithDashes } from 'shared/utils/formatStringLowercaseWithDashes';
import { normalizeValue } from 'shared/utils/normalizeValue';
import { startCase } from 'shared/utils/startCase';
import { useStoreSelector } from 'store/hooks/storeHooks';
import { variableFontRegular, variableFont } from 'styles/theme/text';

interface IProductBreadcrumb {
  addSeparator?: boolean;
  href: string;
  text: string;
}

const ProductBreadcrumb = ({ addSeparator = false, href, text }: IProductBreadcrumb) => {
  return (
    <ProductBreadcrumb.ListItem>
      <Link href={href} passHref prefetch={false} data-qa="product_breadcrumb_link" legacyBehavior>
        <ProductBreadcrumb.Link data-qa="product_breadcrumb">{text}</ProductBreadcrumb.Link>
      </Link>
      {addSeparator && <span> / </span>}
    </ProductBreadcrumb.ListItem>
  );
};

ProductBreadcrumb.ListItem = styled.li`
  list-style-type: none;
`;

ProductBreadcrumb.Link = styled.a`
  text-decoration: underline;
  text-transform: capitalize;

  @media (max-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    ${variableFont({ height: '14px', size: '10px', fontWeight: '400' })};
  }

  &:hover {
    text-decoration: underline;
  }
`;

export const ProductBreadcrumbs = () => {
  const { brandName, isFashionProduct, productType, silhouette } = useStoreSelector(
    (state) => state.pdp.productTemplate,
  );
  // EVO-395: this is to avoid footer breadcrumbs that 404, we use Constructor's mapping to get the productCategory
  const productCategory = findParentProductCategory(productType);
  const productCategoryType = productCategory === 'clothing' ? 'apparel' : productCategory;
  const normalizedSilhouette = formatStringLowercaseWithDashes(normalizeValue(silhouette));
  const normalizedBrand = formatStringLowercaseWithDashes(normalizeValue(brandName));

  return (
    <ProductBreadcrumbs.Wrapper data-qa="product_breadcrumbs_wrapper">
      {isFashionProduct ? (
        <>
          {normalizedBrand && (
            <>
              <ProductBreadcrumb
                href={`/brand/${normalizedBrand}`}
                text={brandName}
                addSeparator={!!productCategory}
              />
              {productCategory && (
                <ProductBreadcrumb
                  href={`/${productCategoryType}/brand/${normalizedBrand}`}
                  text={startCase(productCategoryType)}
                  addSeparator={!!(productType && normalizedBrand)}
                />
              )}
            </>
          )}
          {productType && productCategory && (
            <ProductBreadcrumb
              href={buildPTProductTypeLink(productCategory, productType, normalizedBrand)}
              text={startCase(productType)}
            />
          )}
        </>
      ) : (
        <>
          {normalizedBrand && (
            <>
              <ProductBreadcrumb href={`/brand/${normalizedBrand}`} text={brandName} addSeparator />
              <ProductBreadcrumb
                href={`/sneakers/brand/${normalizedBrand}`}
                text="sneakers"
                addSeparator={!!(silhouette && normalizedBrand)}
              />
            </>
          )}
          {silhouette && (
            <ProductBreadcrumb
              href={`/sneakers/silhouette/${normalizedSilhouette}`}
              text={silhouette}
            />
          )}
        </>
      )}
    </ProductBreadcrumbs.Wrapper>
  );
};

ProductBreadcrumbs.Wrapper = styled.ol`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  list-style-type: none;
  margin-block-end: 0;
  margin-block-start: 0;
  margin-inline-end: 0;
  margin-inline-start: 0;
  margin: 0;
  padding-inline-start: 0;
  padding-left: 0;
  @media (max-width: ${({ theme }) => theme.breakpoints.mediumSmall - 1}px) {
    padding-top: 8px;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    padding-top: 6px;
  }

  ${variableFontRegular({ height: '12px', size: '9.5px' })};

  span {
    white-space: pre;
  }
`;
